import {
    CreateExamApplication,
    GeneratePaymentUrl,
    IApplicantExamDetails,
    IApplicantExamList,
    IExamApplicationDetails,
    QualificationDocumentTemplateGroup,
    ExamType,
    SubmitExamApplication,
} from '../../api/ApiClient';
import { INotification } from 'components';
import { createModel } from '@rematch/core';
import moment from 'moment';
import apiConnector from '../../api/ApiConnector';

const initialState: ExamState = {
    examsList: null,
    exam: null,
    examApplication: null,
    paymentUrl: null,
    documentTemplateGroups: null,
    onlineExamRedirectionUrl: null,
};

export default createModel({
    state: initialState,
    reducers: {
        setExamState<S extends ExamState, K extends keyof ExamState>(state: S, data: (Pick<S, K> | S | null)) {
            return { ...state, ...data };
        },
        setOnlineExamRedirectionUrl(state, onlineExamRedirectionUrl: string | null) {
            return { ...state, onlineExamRedirectionUrl };
        },
    },
    effects: (dispatch: any) => ({
        async fetchExams(payload: GetExamsQuery) {
            this.setExamState({
                examsList: await apiConnector.execApi(dispatch, apiClient => apiClient.getExams(
                    payload.from,
                    payload.to,
                    payload.examType,
                    payload.page,
                    payload.pageSize,
                    payload.sortOrder,
                    payload.sortField,
                )),
            });
        },
        async uploadExamApplicationDocument(payload: UploadDocumentPayload) {
            this.setExamState({
                examApplication: await apiConnector.execApi(dispatch, api => api.uploadExamApplicationDocumentFix(
                    payload.examApplicationId,
                    payload.templateId,
                    payload.file,
                )),
            });
        },
        async deleteExamApplicationDocument(payload: DeleteDocumentPayload) {
            this.setExamState({
                examApplication: await apiConnector.execApi(dispatch, api => api.deleteExamApplicationDocument(
                    payload.examApplicationId,
                    payload.documentId,
                )),
            });
        },
        async deleteExamApplicationDocumentSourceAsync(payload: DeleteDocumentSource) {
            this.setExamState({
                examApplication: await apiConnector.execApi(dispatch, api => api.deleteExamApplicationDocumentSource(
                    payload.examApplicationId,
                    payload.documentId,
                    payload.sourceFileId,
                )),
            });
        },
        async payForExamApplication(id: string) {
            const paymentUrl = await apiConnector.execApi(dispatch, api => api.generatePaymentUrl(new GeneratePaymentUrl({ examApplicationId: id })));

            if (paymentUrl) {
                this.setExamState({ paymentUrl });
            }
        },
        async submitExamApplication(data: ISubmitExamApplication) {
            const examApplication = await apiConnector.execApi(dispatch, api => api.submitExamApplication(data.examApplicationId, data.body));

            if (examApplication) {
                this.setExamState({ examApplication });
                dispatch.notifications.addNotification([{ message: 'Заявка успешно отправлена на рассмотрение!', appearance: 'success' }] as INotification[]);
            }

            return examApplication;
        },
        async createExamApplication(examId: string) {
            const examApplication = await apiConnector.execApi(dispatch, api => api.createExamApplication(new CreateExamApplication({ examId })));
            this.setExamState({ examApplication });

            return examApplication;
        },
        async fetchExamApplication(id: string) {
            const examApplication = await apiConnector.execApi(dispatch, api => api.getExamApplication(id));
            this.setExamState({ examApplication });

            return examApplication;
        },
        async fetchExamDetails(id: string) {
            const exam = await apiConnector.execApi(dispatch, api => api.getExamDetails(id));
            let app = null;
            if (exam) {
                if (exam.examApplicationId) {
                    const examApplication = await apiConnector.execApi(dispatch, api => api.getExamApplication(String(exam.examApplicationId)));
                    app = examApplication;
                    this.setExamState({
                        exam,
                        examApplication,
                    });
                } else {
                    this.setExamState({
                        exam,
                        examApplication: null,
                    });
                }
            }
            this.setExamState({ exam });

            return app;
        },
        async generateOnlineExamRedirectionUrlAsync(examId: string) {
            const onlineExamRedirectionUrl = await apiConnector.execApi(dispatch, apiClient => apiClient.generateOnlineExamRedirectionUrl(examId));

            this.setOnlineExamRedirectionUrl(onlineExamRedirectionUrl);
        },
    }),
});

interface ExamState {
    examsList: IApplicantExamList | null;
    exam: IApplicantExamDetails | null;
    examApplication: IExamApplicationDetails | null;
    paymentUrl: string | null;
    documentTemplateGroups: QualificationDocumentTemplateGroup[] | null;
    onlineExamRedirectionUrl: string | null;
}

interface DeleteDocumentPayload {
    examApplicationId: string;
    documentId: string;
}

interface UploadDocumentPayload {
    examApplicationId: string;
    templateId: string;
    file: File;
}

interface DeleteDocumentSource {
    examApplicationId: string;
    documentId: string;
    sourceFileId: string;
}

interface GetExamsQuery {
    from?: moment.Moment | null;
    to?: moment.Moment | null;
    examType?: ExamType;
    page?: number;
    pageSize?: number;
    sortOrder?: boolean | null;
    sortField?: string | null;
}

interface ISubmitExamApplication {
    examApplicationId: string;
    body?: SubmitExamApplication;
}
